import type { BalanceData, AprData, LenderConfigMap, PostTradeMetrics } from '../utils'
import { getAssetConfig, computePostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import { getOrganicYieldWithdraw, getRewardYieldWithdraw, getIntrinsicYieldWithdraw } from '../yield'

/**
 * Compute the post-trade balance metrics for a withdraw operation.
 *
 * A withdrawal decreases collateral, borrowDiscountedCollateral, deposits, and nav.
 * When `apr` and `yieldParams` are provided, also computes `aprDataPost`.
 */
export function computeWithdrawDelta(
  amount: number,
  price: number,
  balanceData: BalanceData,
  config: LenderConfigMap,
  modeId: string,
  apr?: AprData,
  yieldParams?: LenderYields,
): PostTradeMetrics {
  const { borrowCollateralFactor, collateralFactor } = getAssetConfig(config, modeId)

  const dollarAmount = amount * price
  const postTrade: BalanceData = {
    ...balanceData,
    borrowDiscountedCollateral:
      balanceData.borrowDiscountedCollateral - borrowCollateralFactor * dollarAmount,
    collateral: balanceData.collateral - collateralFactor * dollarAmount,
    deposits: balanceData.deposits - dollarAmount,
    nav: balanceData.nav - dollarAmount,
  }

  const result = computePostTradeMetrics(balanceData, postTrade)

  if (apr && yieldParams) {
    const cashFlowDeposits = balanceData.deposits * apr.depositApr
    const cashFlowDebt = balanceData.debt * apr.borrowApr
    const rewardFlowDeposits = balanceData.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = balanceData.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = balanceData.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = balanceData.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldWithdraw(
      dollarAmount, cashFlowDeposits, cashFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
    )
    newApr.rewards = getRewardYieldWithdraw(
      dollarAmount, rewardFlowDeposits, rewardFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
    )
    ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
      getIntrinsicYieldWithdraw(
        dollarAmount, intrinsicFlowDeposits, intrinsicFlowDebt, balanceData.deposits, balanceData.debt, yieldParams
      )
    result.post.aprData = newApr
  }

  return result
}
